import React, { useState, useEffect } from 'react';
import ModalBase from './ModalBase';
import FormField from './FormField';
import GenericSelector, { SelectorOption } from './GenericSelector';
import ExplanationField from './ExplanationField';
import {
    faPencil,
    faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import '../styles/components/UploadPackCopy.scss';
import FormFieldLabel from './FormFieldLabel';

export type UploadMode = 'automatic' | 'manual';

const uploadModeOptions: SelectorOption<UploadMode>[] = [
    { value: 'automatic', label: 'Automatic', icon: faWandMagicSparkles },
    { value: 'manual', label: 'Manual', icon: faPencil },
];

interface CreatePackCopyStep1ModalProps {
    onClose: () => void;
    onNext: (data: {
        versionName: string;
        notes: string;
        uploadMode: UploadMode;
    }) => void;
    initialData?: {
        versionName?: string;
        notes?: string;
        uploadMode?: UploadMode | null;
    };
}

const CreatePackCopyStep1Modal: React.FC<CreatePackCopyStep1ModalProps> = ({
    onClose,
    onNext,
    initialData,
}) => {
    const [versionName, setVersionName] = useState(
        initialData?.versionName || '',
    );
    const [notes, setNotes] = useState(initialData?.notes || '');
    const [selectedMode, setSelectedMode] = useState<UploadMode | null>(
        initialData?.uploadMode || null,
    );

    const [canContinue, setCanContinue] = useState(false);

    useEffect(() => {
        setCanContinue(!!versionName.trim() && !!selectedMode);
    }, [versionName, selectedMode]);

    const handleNext = () => {
        if (canContinue && selectedMode) {
            onNext({
                versionName: versionName.trim(),
                notes: notes.trim(),
                uploadMode: selectedMode,
            });
        }
    };

    return (
        <ModalBase onClose={onClose} width="500px">
            <div className="upload-components">
                <h2 className="modal-title">Create New Pack Copy</h2>
                <p className="modal-description">
                    Let&apos;s start by setting up the basic information for
                    your pack copy.
                </p>
                <div className="form-field-container">
                    <FormField
                        id="versionName"
                        label="Version Name"
                        value={versionName}
                        onChange={(e) => setVersionName(e.target.value)}
                        placeholder="Enter a name for this pack copy version"
                        required={true}
                    />

                    <FormField
                        id="notes"
                        label="Notes"
                        type="textarea"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add any notes or comments (optional)"
                        rows={3}
                        required={false}
                    />

                    <div className="upload-mode-section">
                        <FormFieldLabel
                            htmlFor="uploadMode"
                            label="How would you like to create this pack copy?"
                            required={true}
                        />

                        <ExplanationField>
                            <p>
                                <strong>Automatic:</strong> Upload a document
                                and let AI extract the specs automatically.
                            </p>
                            <p>
                                <strong>Manual:</strong> Create specs manually
                                by adding them one by one.
                            </p>
                        </ExplanationField>
                        <GenericSelector
                            options={uploadModeOptions}
                            selectedValue={selectedMode}
                            onSelectValue={setSelectedMode}
                        />
                    </div>
                </div>

                <div className="button-row">
                    <button className="btn-secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleNext}
                        disabled={!canContinue}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </ModalBase>
    );
};

export default CreatePackCopyStep1Modal;
